/**
 * Represents a level on the map screen
 *
 * @params stars - current number of stars for level represented
 * @params idNum - level id number for node text
 * @params gainStars - do not show stars if node is part of map progression
 * @params id - store the current levelId attached to the node
 */
import { TweenLite } from 'gsap';
import getLogger, { ILogger } from '../../Core/Logger';
import { waitForMs } from '../../Utils/AsyncUtils';
import { Star } from '../Animations/Star';
import { UIButton } from '../Shared/Buttons/UIButton';
import { buttonIdleAnimation } from '../Animations/UIAnimations';

const nodeLogger: ILogger = getLogger('[MAP-NODE]:');

export default class MapNode extends PIXI.Sprite {
    public locked: boolean = true;
    public nodeId: string;
    private btn: UIButton;
    private stars: PIXI.Sprite[] = [];
    private nodeNumber: PIXI.extras.BitmapText;
    private nodeSuffix: string = '';

    constructor(
        stars: number,
        id: string,
        idNum: number,
        gainStars: number = 0,
        goal: string = 'all',
        timed: boolean = false
    ) {
        super();

        this.nodeNumber = new PIXI.extras.BitmapText(idNum.toString(10), {
            align: 'center',
            font: '70px riffic',
        });
        this.nodeNumber.anchor = 0.5;
        this.nodeNumber.scale.set(0.5, 0.4);
        this.nodeNumber.y = this.nodeNumber.y - this.nodeNumber.height / 2;
        // will be adding more types
        const goalSuffix = goal === 'toys' ? '-goal' : goal === 'pair' ? '' : '-' + goal;
        this.nodeSuffix = timed ? '-time' : goalSuffix;
        const locked = stars === 0;
        if (locked) {
            this.nodeNumber.tint = 0x5c80a0;
        } else {
            this.nodeNumber.tint = this.getActiveTextColor();
        }
        const textureId = 'level-node' + this.nodeSuffix + (locked ? '-locked' : '');
        this.btn = new UIButton('mapui/' + textureId);
        this.btn.scale.set(2);
        this.btn.originalScale.set(this.btn.scale.x, this.btn.scale.y);
        this.addChild(this.btn);
        this.btn.addChild(this.nodeNumber);

        if (stars - gainStars > 0) {
            while (this.stars.length < stars - gainStars) {
                const s = this.addStar(false);
                s.catch(err => {
                    nodeLogger.debug('error adding star: ' + err);
                });
            }
        }

        this.nodeId = id;
        this.interactive = true;
        this.buttonMode = true;
    }

    public setActive() {
        this.btn.bg.texture =
            PIXI.utils.TextureCache['mapui/level-node' + this.nodeSuffix + '-current'];

        const animationSpeed = 0.7;
        const animationSprite = new PIXI.Sprite(PIXI.utils.TextureCache['mapui/level-node-locked']);
        animationSprite.anchor.set(0.5);
        this.btn.addChild(animationSprite);
        TweenLite.to(animationSprite, animationSpeed, {
            alpha: 0,
            onComplete: () => {
                this.btn.removeChild(animationSprite);
                animationSprite.destroy();
            },
        });

        this.nodeNumber.tint = this.getActiveTextColor();

        const glow = new PIXI.Sprite(
            PIXI.utils.TextureCache['mapui/level-node' + this.nodeSuffix + '-current-glow']
        );
        glow.alpha = 0;
        glow.anchor.set(0.5);
        glow.scale.set(2);
        this.addChildAt(glow, 0);
        TweenLite.to(glow, animationSpeed, {
            alpha: 1,
            onComplete: () => buttonIdleAnimation(this.btn, 1),
        });
    }

    public async showScore(score: number) {
        let newLen = this.stars.length + score;
        if (newLen > 3) {
            newLen = 3;
        }
        await waitForMs(600);
        while (this.stars.length < newLen) {
            await this.addStar(true);
        }

        return;
    }

    public async addStar(animate?: boolean) {
        const s = new PIXI.Sprite(
            PIXI.utils.TextureCache['mapui/level-node-star0' + (this.stars.length + 1).toString(10)]
        );
        s.anchor.set(0.5);
        s.scale.set(2);
        if (animate) {
            this.addChild(s);
            this.stars.push(s);
            const starAnim = new Star(1);
            const starAnimSprite = starAnim.sprite;
            starAnimSprite.x =
                this.stars.length === 1
                    ? -this.btn.width * 0.3
                    : this.stars.length === 2
                        ? 0
                        : this.btn.width * 0.3;
            starAnimSprite.y = this.btn.height * 0.25;
            this.addChild(starAnimSprite);
            starAnim.play();
            await waitForMs(400);
        } else {
            this.addChild(s);
            this.stars.push(s);
        }

        return;
    }

    private getActiveTextColor() {
        if (this.nodeSuffix.includes('time')) {
            return 0x217f0d;
        }

        if (this.nodeSuffix.includes('goal')) {
            return 0xbc00d8;
        }

        if (this.nodeSuffix.includes('hard')) {
            return 0xe52e2e;
        }

        if (this.nodeSuffix.includes('boss')) {
            return 0x8400c8;
        }

        if (this.nodeSuffix.includes('gold')) {
            return 0xcc7700;
        }

        return 0x006eba;
    }
}
